import { useState } from "react";

import { queryKeys } from "@/api/hooks";
import {
  useAssetAdoptionsQuery,
  useAssetVersionQuery,
  useAssetVersionsQuery,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { formatDate } from "@/lib/format";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { ErrorState, LoadingState } from "@/ui/states";

import { AdoptionImpact } from "./AdoptionImpact";
import { VersionContent } from "./VersionContent";

/**
 * The audit floor shared by every full library-asset detail page.
 *
 * A dedicated semantic editor may replace the generic authoring card, but it
 * must not replace the immutable evidence around that card. Keeping adoption,
 * version history, and historical-content inspection in one component prevents
 * a named editor from silently making older versions or project pins
 * unreachable when it takes ownership of the page chrome.
 */
export function AssetAuditTrail({ assetId }: { assetId: string }) {
  const versions = useAssetVersionsQuery(assetId);
  const adoptions = useAssetAdoptionsQuery(assetId);
  const retryAdoptions = useRetryPausedQuery(
    queryKeys.assetAdoptions(assetId),
  );
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const versionDetail = useAssetVersionQuery(assetId, selectedVersion);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Adoption</CardTitle>
        </CardHeader>
        <CardBody>
          <AdoptionImpact adoptions={adoptions} retry={retryAdoptions} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Version history</CardTitle>
        </CardHeader>
        {versions.isPending ? (
          <CardBody>
            <LoadingState label="Loading version history" rows={3} />
          </CardBody>
        ) : versions.isError ? (
          <CardBody>
            <ErrorState
              title="Version history could not be loaded"
              description="The Editor Service did not return this asset's versions."
              onRetry={() => void versions.refetch()}
            />
          </CardBody>
        ) : (
          <ul>
            {versions.data.items.map((version) => (
              <li
                key={version.version}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-5 py-3 text-sm last:border-b-0"
                aria-current={
                  selectedVersion === version.version ? "true" : undefined
                }
              >
                <span className="w-10 font-mono text-foreground">
                  {version.version}
                </span>
                <span className="text-muted-foreground">
                  {formatDate(version.createdAt)}
                </span>
                <span className="text-muted-foreground">
                  {version.createdBy}
                </span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {version.note ?? "—"}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-pressed={selectedVersion === version.version}
                  onClick={() => setSelectedVersion(version.version)}
                >
                  View {version.version}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {selectedVersion !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>Content at {selectedVersion} (read-only)</CardTitle>
          </CardHeader>
          <CardBody>
            {versionDetail.isPending ? (
              <LoadingState
                label={`Loading content at ${selectedVersion}`}
                rows={3}
              />
            ) : versionDetail.isError ? (
              <ErrorState
                title="This version's content could not be loaded"
                description="The Editor Service did not return the content at this version."
                onRetry={() => void versionDetail.refetch()}
              />
            ) : (
              <VersionContent content={versionDetail.data.content} />
            )}
          </CardBody>
        </Card>
      ) : null}
    </>
  );
}
